import { useState } from 'react';
import useApi from 'hooks/useApi';

interface UseLocationOffboardParams {
  locationId: number;
  offboardReason: string;
  action: 'offboard' | 'reason_only';
  deactivateCompany?: boolean;
  onSuccess?: (response: UseLocationOffboardResponse) => void;
  onError?: (error: string) => void;
}

interface UseLocationOffboardResponse {
  action: string;
  locationId: number;
}

const useLocationOffboard = () => {
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<UseLocationOffboardResponse | null>(null);

  const offboardLocation = async ({
    locationId,
    offboardReason,
    action,
    deactivateCompany = false,
    onSuccess,
    onError,
  }: UseLocationOffboardParams) => {
    setLoading(true);
    setError(null);

    try {
      const apiResponse = await api({
        url: `/v3/admin/locations/${locationId}/offboard`,
        method: 'PUT',
        data: {
          location: {
            action,
            offboard_reason: offboardReason,
            deactivate_company: deactivateCompany ? 1 : 0,
          },
        },
      });

      if (apiResponse.data?.data) {
        const responseData = apiResponse.data.data;

        const formattedResponse: UseLocationOffboardResponse = {
          action: responseData.action,
          locationId: responseData.location_id,
        };

        setResponse(formattedResponse);

        if (onSuccess) {
          onSuccess(formattedResponse);
        }

        return formattedResponse;
      }

      throw new Error('Unexpected response structure');
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        'Failed to offboard location. Please try again.';

      setError(errorMessage);

      if (onError) {
        onError(errorMessage);
      }

      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return {
    offboardLocation,
    loading,
    error,
    response,
  };
};

export default useLocationOffboard;
